// ============================================================================
// OMS Returns + Refund API — thin axios wrappers over the DRF endpoints landed
// in oms/api/views/returns.py (ADR 2026-10-01 Task #16). The services on the
// backend are fat; these functions just send the right shape and surface errors.
// ============================================================================
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { errorActions } from "../../../helpers/auth_helper";

// ---------------------------------------------------------------------------
// Returns tab list
// ---------------------------------------------------------------------------

export type ReturnRowStatus =
  | "pending_booking"             // stage exists, carrier not yet called
  | "booked"                      // carrier accepted (provider_ref assigned)
  | "picked_up"                   // driver physically collected; in transit to WH
  | "received"                    // WH acknowledged receipt
  | "qc_done"                     // WH stamped QC (received_qty + breakage)
  | "authorized";                 // admin acted; downstream money chain running

// Mirrors the Last Mile board row's action shape — server-authored, FE never hard-codes routes.
export interface ReturnRowAction {
  action: string;                 // e.g. "mark_in_transit", "mark_delivered"
  label: string;
  href: string;
}

export interface ReturnRowLogistics {
  id: number;                     // LogisticsDocument.id — the key for reassign + status actions
  provider_type: string;          // e.g. "own_tms", "porter", "delhivery"
  provider_ref: string | null;
  lifecycle: string;              // draft / active / short_closed / cancelled / superseded
  awb_no: string | null;
  tracking_url: string | null;
  available_actions: ReturnRowAction[];
  vehicle_number?: string | null;
  driver_name?: string | null;
  driver_number?: string | null;
  trip_name?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
}

export interface OpenEscalation {
  issue_type: string | null;
  category: string | null;
  remarks: string;
  raised_at: string | null;
}

// One item on the return stage — picked up from the customer. Rendered as a row in the
// shortfall-at-pickup form so the operator can edit the actual picked qty down per item.
export interface ReturnStageItem {
  allocation_item_id: number;
  product_name: string;
  sku?: string | null;
  picked_qty: string | number;
  // Set once QC is done: what actually arrived, and whether QC flagged it broken.
  received_qty?: string | null;
  breakage?: boolean;
}

export interface ReturnRow {
  stage_id: number;
  md_number: string | null;
  so_id: number;
  so_number: string | number;
  // Same city desks as the logistics board's city filter: "Bangalore" / "Hyderabad" by pincode belt,
  // "outstation" when no hub serves the ship-to; null when neither.
  delivery_city: string | null;
  status: ReturnRowStatus;
  received: boolean;
  qc_completed: boolean;
  authorized: boolean;
  destination_wh_id: number | null;
  // Who QCs the return at its receiving warehouse (fixed per WH); null for an unmapped WH.
  qc_owner?: { id: string; name: string | null } | null;
  created_at: string | null;
  // Operator-chosen return pickup date (YYYY-MM-DD) — captured on the Return dialog and used
  // by Loginext auto-booking as the pickup window start.
  pickup_date: string | null;
  // Movement — from = customer address, to = MD WH destination
  origin_label: string | null;
  destination_label: string | null;
  // Escalation context (IssueDrawer target reads these)
  client_name: string | null;
  client_contact?: string | null;
  client_alternate_contact?: string | null;
  // Precise pins for the From / To map links (from the stage's endpoint snapshots).
  origin_lat?: number | null;
  origin_long?: number | null;
  destination_lat?: number | null;
  destination_long?: number | null;
  lead_id?: string | null;
  // Order carries an unloading charge — labour needed at the customer's site for the pickup.
  is_unloading?: boolean;
  loading_required?: boolean | null;
  pickup_floor?: number | string | null;
  pickup_has_lift?: boolean | null;
  // Customer's user id — the Authorize modal uses it to list/add the customer's bank accounts.
  user_id: string | null;
  po_numbers: string[];
  // null when the carrier booking hasn't been created yet (status=pending_booking)
  logistics: ReturnRowLogistics | null;
  // The latest UNRESOLVED issue_raised event on the stage, if any. Rendered as a chip
  // on the row — same role the Last Mile board's "Escalation" column plays.
  escalation: OpenEscalation | null;
  // Per-item lines on the stage. Populated by the ReturnRow serializer's _items_context
  // helper; drives the shortfall-at-pickup form. May be undefined on older server builds.
  items?: ReturnStageItem[];
  vendor_return?: VendorReturnSummary | null;
}

export type VendorReturnMode = "ship" | "collect";

export type VendorReturnStatus =
  | "awaiting_stock"
  | "awaiting_bill"
  | "filing_credit"
  | "credit_failed"
  | "credit_filed"
  | "completed"
  | "cancelled";

export interface VendorReturnSummary {
  stage_id: number;
  mode: VendorReturnMode;
  status: VendorReturnStatus;
  issue_state: string;
  hold_reason: string | null;
  vendor_credit_id: string | null;
  vendor_credit_number: string | null;
  vendor_credit_error: string | null;
  bill_applied: { bill_id: string; amount?: string; error?: string }[] | null;
  items: { allocation_item_id: number; qty: string; rate: string }[];
  logistics?: ReturnRowLogistics | null;
}

export interface VendorReturnContextItem {
  allocation_item_id: number;
  product_name: string;
  sku: string | null;
  received_qty: string;
  default_qty: string;
  po_rate: string;
  po_billed_status: string | null;
}

export interface VendorReturnContext {
  return_stage_id: number;
  eligible: boolean;
  blocked_reason: string | null;
  mode: VendorReturnMode | null;
  supplier: { id: number; name: string | null } | null;
  supplier_warehouse: string | null;
  po_number: string | null;
  items: VendorReturnContextItem[];
  vendor_return: VendorReturnSummary | null;
}

export interface ReturnsListResponse {
  count: number;
  results: ReturnRow[];
}

export type ReturnsListStatusFilter =
  | "all_pending"               // default — every live return not yet authorized (the full worklist)
  | "pickup_pending"            // not yet picked up, booked or not (= status "pending_booking" / "booked")
  | "receive_pending"           // raised but not received at the WH yet (ops expects a truck)
  | "qc_pending"                // received, QC not stamped
  | "authorize_pending"         // QC done, admin needs to authorize the refund
  | "authorized"                // admin acted; downstream money chain running
  | "escalated"                 // open issue_raised on the stage (unresolved)
  | "vendor_return"             // a vendor return has been raised
  | "all";                      // everything live, authorized or not

export async function listReturns(
  statusFilter: ReturnsListStatusFilter = "all_pending"
): Promise<ReturnsListResponse | undefined> {
  try {
    return await axios
      .get("/oms/returns/", { params: { status: statusFilter } })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading returns");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Shortfall at pickup — driver reports partial pickup; CS edits picked qty down per item.
// Shrinks the stage's OperationItem.qty + raises a RETURN_SHORT_AT_PICKUP escalation.
// ---------------------------------------------------------------------------

export interface ShortfallAtPickupItem {
  allocation_item_id: number;
  actual_qty: string | number;
}

export interface ShortfallAtPickupInput {
  items: ShortfallAtPickupItem[];
  note?: string;
}

export async function raiseReturnShortfallAtPickup(
  stageId: number,
  input: ShortfallAtPickupInput
): Promise<boolean> {
  try {
    const res = await axios.post(
      `/oms/returns/${stageId}/shortfall-at-pickup/`,
      input
    );
    return res?.status === 200 || res?.status === 201;
  } catch (error: any) {
    errorActions(error, "Report shortfall failed");
    return false;
  }
}

// ---------------------------------------------------------------------------
// Mark QC complete — WH action after the return has been received
// ---------------------------------------------------------------------------

export interface QcCompleteInput {
  note?: string;
  received_qty?: Record<string, string | number>;    // keyed on allocation_item_id
  breakage?: Record<string, boolean>;
}

export async function markQcComplete(
  stageId: number,
  input: QcCompleteInput = {}
): Promise<boolean> {
  try {
    const res = await axios.post(`/oms/returns/${stageId}/qc-complete/`, input);
    return res?.status === 200;
  } catch (error: any) {
    errorActions(error, "Mark QC done failed");
    return false;
  }
}

// ---------------------------------------------------------------------------
// Authorize — admin confirms refund amount + destination for a QCed return
// ---------------------------------------------------------------------------

export type RefundDestination = "wallet" | "customer";

// One row the modal sends per editable line. `unit_rate` is tax-INCLUSIVE rupees per unit —
// the figure that lives on the invoice. Backend divides by (1 + tax%/100) to get the ex-tax rate
// for the Zoho CN, and drops the SO's own discount for that line (the operator's number is
// authoritative). See oms/returns/services/authorize.py + file_return_cn.py.
export interface AuthorizeReturnItem {
  allocation_item_id: number;
  qty: string | number;
  unit_rate: string | number;
}

export interface AuthorizeReturnInput {
  amount: string | number;
  destination: RefundDestination;
  bank_details_id?: number | null;
  note?: string;
  file_cn_now?: boolean;
  autonumber_group_id?: string;
  place_of_supply?: string;
  // Per-line qty + tax-inclusive rate overrides. When supplied, the backend recomputes `amount`
  // from Σ(qty × unit_rate) and uses these rates on the Zoho CN. Omit for the back-compat
  // amount-only path (older modal).
  items?: AuthorizeReturnItem[];
  // Unified picker payload. Carries `new_bank_details` for an inline-added account, which the
  // server saves inside the Authorize transaction.
  destination_block?: {
    destination: "wallet" | "customer" | "dont_know";
    bank_details_id?: number;
    new_bank_details?: { account_holder_name: string; account_number: string; ifsc: string; bank_name: string };
  };
}

// ---------------------------------------------------------------------------
// Authorize-context — three-column items the modal reads to prefill its form.
// ---------------------------------------------------------------------------

export interface AuthorizeItemRow {
  allocation_item_id: number;
  product_name: string;
  originally_returned_qty: string;   // customer's original return qty for this item
  pickup_shortfall_qty: string;      // what the driver lost at pickup (0 if none)
  qc_shortfall_qty: string;          // what QC rejected (0 if none)
  shortfall_qty: string;             // pickup + QC
  suggested_final_qty: string;       // original − shortfall; modal prefills Final with this
  unit_rate: string;                 // customer-paid rate per unit — tax-INCLUSIVE post-discount
                                     //  when `rate_source === "live_so"`, EstimateItem.unit_selling_price
                                     //  (pre-GST, pre-order-discount) on the fallback path.
  // "live_so" = tax-inclusive post-discount paid per unit, computed from the live Zoho SO line;
  // "estimate_fallback" = pre-GST pre-order-discount from EstimateItem.unit_selling_price, used
  // when the live SO can't be read. The modal shows a warning on the fallback path so the operator
  // knows to double-check the prefill before submitting.
  rate_source?: "live_so" | "estimate_fallback";
  subtotal: string;                  // suggested_final_qty × unit_rate
}

export interface AuthorizeContextResponse {
  stage_id: number;
  items: AuthorizeItemRow[];
  totals: {
    shortfall_qty: string;
    suggested_final_qty: string;
    amount: string;                  // Σ subtotal — the prefilled refund total
  };
}

export async function getAuthorizeContext(
  stageId: number
): Promise<AuthorizeContextResponse | undefined> {
  try {
    return await axios
      .get(`/oms/returns/${stageId}/authorize-context/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading authorize context");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export interface AuthorizeReturnResponse {
  refund_flow_escalation_id: number;
  params: Record<string, unknown>;
}

export async function authorizeReturn(
  stageId: number,
  input: AuthorizeReturnInput
): Promise<AuthorizeReturnResponse | undefined> {
  try {
    return await axios
      .post(`/oms/returns/${stageId}/authorize/`, input)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Authorize failed");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Cancel-before-pickup — abort a RETURN_RECEIVE stage whose PICKED_UP webhook
// has not fired yet. Cancels the LogiNext booking + releases the returnable qty
// back on the SO line. Gated by `return.cancel_before_pickup`.
// ---------------------------------------------------------------------------

export async function cancelReturnBeforePickup(
  stageId: number,
  input: { reason?: string } = {}
): Promise<boolean> {
  try {
    const res = await axios.post(
      `/oms/returns/${stageId}/cancel-before-pickup/`,
      input
    );
    return res?.status === 200 || res?.status === 201;
  } catch (error: any) {
    errorActions(error, "Cancel return failed");
    return false;
  }
}

// Past 4:30 PM IST a pickup can't be booked for today; "allow today" overrides it for an escalated one.
export const pickupCutoffPassed = (): boolean => {
  const ist = dayjs().add(dayjs().utcOffset() * -1 + 330, "minute");
  return ist.hour() * 60 + ist.minute() >= 16 * 60 + 30;
};

export const isPickupDayDisabled = (d: Dayjs, allowToday: boolean): boolean =>
  !!d && (d.isBefore(dayjs(), "day") ||
    (d.isSame(dayjs(), "day") && pickupCutoffPassed() && !allowToday));

// Outstation return pickups are planned D+3 working days out (backend `sla.earliest_return_pickup`;
// holidays are applied there, Sundays here).
export const OUTSTATION_RETURN_SLA_DAYS = 3;

export const earliestOutstationPickup = (): Dayjs => {
  let d = dayjs().startOf("day");
  for (let n = 0; n < OUTSTATION_RETURN_SLA_DAYS; ) {
    d = d.add(1, "day");
    if (d.day() !== 0) n += 1;
  }
  return d;
};

export async function rescheduleReturnPickup(
  stageId: number,
  input: { pickup_date: string; reason?: string; reattempt?: boolean; allow_today?: boolean }
): Promise<boolean> {
  try {
    const res = await axios.post(`/oms/returns/${stageId}/reschedule-pickup/`, input);
    return res?.status === 200;
  } catch (error: any) {
    errorActions(error, input.reattempt ? "Reattempt pickup failed" : "Reschedule pickup failed");
    return false;
  }
}

export async function changeReturnWarehouse(
  stageId: number,
  input: { warehouse_id: number; reason?: string }
): Promise<boolean> {
  try {
    const res = await axios.post(`/oms/returns/${stageId}/change-warehouse/`, input);
    return res?.status === 200;
  } catch (error: any) {
    errorActions(error, "Change warehouse failed");
    return false;
  }
}

// ---------------------------------------------------------------------------
// Return to vendor — gated by `return.to_vendor`
// ---------------------------------------------------------------------------

export async function getVendorReturnContext(
  stageId: number
): Promise<VendorReturnContext | undefined> {
  try {
    const res = await axios.get(`/oms/returns/${stageId}/vendor-return-context/`);
    return res?.data;
  } catch (error: any) {
    errorActions(error, "Error loading vendor return details");
    return undefined;
  }
}

export async function raiseVendorReturn(
  stageId: number,
  input: { items: { allocation_item_id: number; qty: string | number; rate: string | number }[]; note?: string }
): Promise<VendorReturnSummary | undefined> {
  try {
    const res = await axios.post(`/oms/returns/${stageId}/vendor-return/`, input);
    return res?.data;
  } catch (error: any) {
    errorActions(error, "Return to vendor failed");
    return undefined;
  }
}

export async function markVendorReturnDone(vendorStageId: number): Promise<boolean> {
  try {
    const res = await axios.post(`/oms/vendor-returns/${vendorStageId}/mark-done/`);
    return res?.status === 200;
  } catch (error: any) {
    errorActions(error, "Update vendor return failed");
    return false;
  }
}

export async function cancelVendorReturn(
  vendorStageId: number,
  input: { reason?: string } = {}
): Promise<boolean> {
  try {
    const res = await axios.post(`/oms/vendor-returns/${vendorStageId}/cancel/`, input);
    return res?.status === 200;
  } catch (error: any) {
    errorActions(error, "Cancel vendor return failed");
    return false;
  }
}

// ---------------------------------------------------------------------------
// Bank details — list + add for a specific user
// ---------------------------------------------------------------------------

export interface UserBankDetails {
  id: number;
  account_number: string;
  ifsc: string;
  account_holder_name: string;
  bank_name: string;
  is_active?: boolean;
}

export async function listUserBankDetails(
  userId: string
): Promise<UserBankDetails[]> {
  try {
    const resp = await axios.get(`/oms/users/${userId}/bank-details/`);
    return resp?.data?.results ?? [];
  } catch (error: any) {
    errorActions(error, "Error loading bank details");
    return [];
  }
}

export interface AddUserBankDetailsInput {
  account_number: string;
  ifsc: string;
  account_holder_name: string;
  bank_name: string;
}

export async function addUserBankDetails(
  userId: string,
  input: AddUserBankDetailsInput
): Promise<UserBankDetails | undefined> {
  try {
    return await axios
      .post(`/oms/users/${userId}/bank-details/`, input)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Add bank account failed");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Cancel refund — releases earmark after live Zoho CN refund-status check
// ---------------------------------------------------------------------------

export interface CancelRefundResponse {
  cancelled: boolean;
  released_amount: string;
  wallet_available_balance: string;
}

export async function cancelRefund(
  walletTxId: number
): Promise<CancelRefundResponse | undefined> {
  try {
    return await axios
      .post(`/oms/refunds/${walletTxId}/cancel/`)
      .then((res) => res?.data)
      .catch((error) => {
        // 409 = Zoho already applied the refund; surface the message, not a toast.
        if (error?.response?.status === 409) {
          return undefined;
        }
        errorActions(error, "Cancel refund failed");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Bank catalogue — the 33 RBI-licensed banks the modal's dropdown shows.
// Mirrors oms.returns.models.Bank TextChoices on the backend. Keep in sync.
// ---------------------------------------------------------------------------

export const BANK_CHOICES: { value: string; label: string }[] = [
  // PSU
  { value: "STATE_BANK_OF_INDIA", label: "State Bank of India (SBI)" },
  { value: "BANK_OF_BARODA", label: "Bank of Baroda" },
  { value: "PUNJAB_NATIONAL_BANK", label: "Punjab National Bank (PNB)" },
  { value: "CANARA_BANK", label: "Canara Bank" },
  { value: "UNION_BANK_OF_INDIA", label: "Union Bank of India" },
  { value: "BANK_OF_INDIA", label: "Bank of India" },
  { value: "INDIAN_BANK", label: "Indian Bank" },
  { value: "CENTRAL_BANK_OF_INDIA", label: "Central Bank of India" },
  { value: "INDIAN_OVERSEAS_BANK", label: "Indian Overseas Bank" },
  { value: "UCO_BANK", label: "UCO Bank" },
  { value: "BANK_OF_MAHARASHTRA", label: "Bank of Maharashtra" },
  { value: "PUNJAB_AND_SIND_BANK", label: "Punjab & Sind Bank" },
  // Private
  { value: "HDFC_BANK", label: "HDFC Bank" },
  { value: "ICICI_BANK", label: "ICICI Bank" },
  { value: "AXIS_BANK", label: "Axis Bank" },
  { value: "KOTAK_MAHINDRA_BANK", label: "Kotak Mahindra Bank" },
  { value: "INDUSIND_BANK", label: "IndusInd Bank" },
  { value: "IDBI_BANK", label: "IDBI Bank" },
  { value: "BANDHAN_BANK", label: "Bandhan Bank" },
  { value: "IDFC_FIRST_BANK", label: "IDFC FIRST Bank" },
  { value: "FEDERAL_BANK", label: "Federal Bank" },
  { value: "RBL_BANK", label: "RBL Bank" },
  { value: "YES_BANK", label: "Yes Bank" },
  { value: "CSB_BANK", label: "CSB Bank" },
  { value: "CITY_UNION_BANK", label: "City Union Bank" },
  { value: "DCB_BANK", label: "DCB Bank" },
  { value: "DHANLAXMI_BANK", label: "Dhanlaxmi Bank" },
  { value: "JAMMU_AND_KASHMIR_BANK", label: "Jammu & Kashmir Bank" },
  { value: "KARNATAKA_BANK", label: "Karnataka Bank" },
  { value: "KARUR_VYSYA_BANK", label: "Karur Vysya Bank" },
  { value: "NAINITAL_BANK", label: "Nainital Bank" },
  { value: "SOUTH_INDIAN_BANK", label: "South Indian Bank" },
  { value: "TAMILNAD_MERCANTILE_BANK", label: "Tamilnad Mercantile Bank" },
];

// IFSC format: 4 letters + "0" + 6 alphanumerics. Mirrors the backend regex validator.
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
